import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, MapPin, Star, Phone, Clock, X, Loader2 } from 'lucide-react';

interface BusinessResult {
  name: string;
  address: string;
  rating: number;
  totalReviews: number;
  phone?: string;
  hours?: string;
  placeId: string;
  type: 'hospital' | 'clinic' | 'individual';
}

interface SmartSearchFieldProps {
  onBusinessSelected: (business: BusinessResult) => void;
}

const SmartSearchField: React.FC<SmartSearchFieldProps> = ({ onBusinessSelected }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<BusinessResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchStatus, setSearchStatus] = useState<'idle' | 'typing' | 'searching' | 'results' | 'error'>('idle');
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessResult | null>(null);
  const [localCache, setLocalCache] = useState<{[key: string]: BusinessResult[]}>({});
  const [isMobileInputFocused, setIsMobileInputFocused] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Hook para detectar dispositivos móveis e teclado virtual
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                       window.innerWidth <= 768;
      setIsMobileDevice(isMobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Hook para detectar quando teclado virtual aparece/desaparece
  useEffect(() => {
    if (!isMobileDevice) return;

    const handleVisualViewportChange = () => {
      if ('visualViewport' in window) {
        const viewport = window.visualViewport;
        const heightDifference = window.innerHeight - (viewport?.height || window.innerHeight);
        // Se altura diminuiu mais que 150px, provavelmente teclado apareceu
        const keyboardVisible = heightDifference > 150;
        
        if (isMobileInputFocused && keyboardVisible) {
          // Teclado ativo e input focado - aplicar modo fixo
          document.body.style.position = 'fixed';
          document.body.style.top = '0';
          document.body.style.left = '0';
          document.body.style.right = '0';
        }
      }
    };

    if ('visualViewport' in window) {
      window.visualViewport?.addEventListener('resize', handleVisualViewportChange);
      return () => window.visualViewport?.removeEventListener('resize', handleVisualViewportChange);
    }
  }, [isMobileDevice, isMobileInputFocused]);

  // Hook para preservar estado mobile durante toda a sessão de busca
  useEffect(() => {
    if (isMobileDevice && inputRef.current === document.activeElement) {
      // Se o input está focado no mobile, garantir que o estado permaneça ativo
      if (!isMobileInputFocused) {
        setIsMobileInputFocused(true);
      }
    }
  }, [isMobileDevice, isMobileInputFocused]);

  // Hook inteligente para detectar cliques fora - apenas se não há seleção em andamento
  useEffect(() => {
    if (!isMobileDevice || !isMobileInputFocused) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Só sair do modo mobile se realmente clicou FORA e não há sugestões sendo processadas
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Verificar se não é uma seleção em andamento
        const isSelectingBusiness = suggestions.length > 0 || searchStatus === 'searching';
        
        if (!isSelectingBusiness) {
          // Clique realmente fora - sair do modo mobile
          setIsMobileInputFocused(false);
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.left = '';
          document.body.style.right = '';
        }
      }
    };

    // Delay maior para evitar conflitos com seleções
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileDevice, isMobileInputFocused, suggestions.length, searchStatus]);

  const detectBusinessType = useCallback((businessName: string): 'hospital' | 'clinic' | 'individual' => {
    // Sempre retorna 'individual' para permitir seleção de qualquer resultado
    // A detecção é mantida apenas para fins de exibição visual
    return 'individual';
  }, []);

  const searchBusinesses = useCallback(async (query: string): Promise<BusinessResult[]> => {
    try {
      console.log(`🔍 Buscando via API: "${query}"`);
      
      const response = await fetch('/api/google-maps/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: query.trim(),
          location: 'Brasil'
        }),
        signal: abortControllerRef.current?.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.data && Array.isArray(data.data)) {
        console.log(`✅ API retornou ${data.data.length} resultados`);
        
        return data.data.map((result: any) => ({
          name: result.name,
          address: result.address,
          rating: result.rating || 0,
          totalReviews: result.totalReviews || 0,
          phone: result.phone,
          hours: result.hours,
          placeId: result.placeId,
          type: result.type || 'individual'
        }));
      }

      return [];
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('❌ Erro na busca API:', error);
        
        // Fallback para dados mock se API falhar
        console.log('🔄 Usando fallback mock');
        const mockResults = [
          {
            name: `Dr. João Silva - ${query}`,
            address: 'Rua das Flores, 123 - Centro, São Paulo - SP',
            rating: 4.8,
            totalReviews: 156,
            phone: '(11) 3456-7890',
            hours: 'Seg-Sex: 8h-18h',
            placeId: 'fallback_1',
            type: 'individual' as const
          },
          {
            name: `Clínica ${query}`,
            address: 'Av. Paulista, 456 - Bela Vista, São Paulo - SP',
            rating: 4.5,
            totalReviews: 89,
            phone: '(11) 2345-6789',
            hours: 'Seg-Sáb: 7h-19h',
            placeId: 'fallback_2',
            type: 'clinic' as const
          }
        ];
        
        return mockResults;
      }
      return [];
    }
  }, [detectBusinessType]);

  const debouncedSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSuggestions([]);
      setSearchStatus('idle');
      setIsLoading(false);
      return;
    }

    // 1. Busca instantânea no cache local primeiro
    const cacheKey = searchQuery.toLowerCase().trim();
    if (localCache[cacheKey]) {
      console.log('⚡ Resultado instantâneo do cache local');
      setSuggestions(localCache[cacheKey]);
      setSearchStatus('results');
      setIsLoading(false);
      return;
    }

    // 2. Mostrar loading profissional quando usuário parou de digitar
    const loadingResult: BusinessResult = {
      name: `🔍 Buscando profissionais para "${searchQuery}"`,
      address: 'Por favor aguarde, estamos buscando os melhores profissionais...',
      rating: 0,
      totalReviews: 0,
      placeId: 'searching_placeholder',
      type: 'individual' as const
    };
    
    setSuggestions([loadingResult]);
    setSearchStatus('searching');
    setIsLoading(true);

    // Cancelar request anterior se existir
    if (abortControllerRef.current) {
      console.log('🚫 Cancelando busca anterior...');
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      console.log(`🚀 Iniciando busca API: "${searchQuery}"`);
      const results = await searchBusinesses(searchQuery);
      
      // Salvar no cache local para próximas buscas
      if (results.length > 0) {
        setLocalCache(prev => ({...prev, [cacheKey]: results}));
      }
      
      setSuggestions(results);
      if (results.length > 0) {
        setSearchStatus('results');
        setIsLoading(false); // Parar loading quando resultados chegam
      } else {
        // Se não encontrou resultados, manter loading por mais tempo antes de mostrar "não encontrado"
        setTimeout(() => {
          if (suggestions.length === 0 || suggestions[0]?.placeId === 'searching_placeholder') {
            setSuggestions([]);
            setSearchStatus('error');
            setIsLoading(false); // Parar loading quando mostrar erro
          }
        }, 5000); // Aumentado para 5 segundos
      }
      
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Search error:', error);
        setSuggestions([]);
        setSearchStatus('error');
        setIsLoading(false);
      }
    }
    // Não colocar setIsLoading(false) no finally para manter loading durante busca
  }, [searchBusinesses]);

  // Effect for debounced search
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Só buscar se não há negócio selecionado e tem query
    if (query.trim() && !selectedBusiness) {
      debounceTimeoutRef.current = setTimeout(() => {
        debouncedSearch(query);
      }, 300); // Reduzido drasticamente para resposta mais rápida
    } else {
      setSuggestions([]);
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [query, debouncedSearch, selectedBusiness]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setActiveSuggestionIndex(-1);
    
    // Se já tem um negócio selecionado e o usuário está editando, limpar seleção
    if (selectedBusiness && value !== selectedBusiness.name) {
      setSelectedBusiness(null);
      // Garantir que o modo mobile permaneça ativo durante edição
      if (isMobileDevice && !isMobileInputFocused) {
        setIsMobileInputFocused(true);
      }
    }
    
    // Só buscar se não há negócio selecionado
    if (!selectedBusiness) {
      if (value.length >= 2) {
        setSearchStatus('typing');
      } else {
        setSearchStatus('idle');
        setSuggestions([]);
      }
    }
  };

  const handleInputFocus = () => {
    if (isMobileDevice) {
      setIsMobileInputFocused(true);
      // Pequeno delay para aguardar o teclado aparecer
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  };

  // handleInputBlur removido - gerenciamento de blur agora é feito pelo hook de clique fora"

  const handleSuggestionClick = (suggestion: BusinessResult) => {
    // Não permitir clique no placeholder de busca
    if (suggestion.placeId === 'searching_placeholder') {
      return;
    }
    
    // Permite seleção de qualquer resultado da busca
    setQuery(suggestion.name);
    setSuggestions([]); // Fecha o dropdown/modal
    setSelectedBusiness(suggestion);
    setSearchStatus('idle');
    
    // CORREÇÃO: Sair do modo mobile ANTES de fazer blur
    // Isso garante que o container volta ao estado normal primeiro
    if (isMobileDevice && isMobileInputFocused) {
      setIsMobileInputFocused(false);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
    }
    
    // Remover foco do input após pequeno delay para evitar conflitos
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }, 50);
    
    // Callback para o componente pai
    onBusinessSelected(suggestion);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveSuggestionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setActiveSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      
      case 'Enter':
        e.preventDefault();
        if (activeSuggestionIndex >= 0) {
          handleSuggestionClick(suggestions[activeSuggestionIndex]);
        }
        break;
      
      case 'Escape':
        setSuggestions([]);
        setActiveSuggestionIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const clearInput = () => {
    setQuery('');
    setSuggestions([]);
    setSelectedBusiness(null);
    setSearchStatus('idle');
    
    // Focar no input - isso irá ativar automaticamente o modo mobile se necessário
    inputRef.current?.focus();
    
    // Se está no mobile, garantir que o estado permaneça ativo
    if (isMobileDevice) {
      setIsMobileInputFocused(true);
    }
  };

  const formatPhone = (phone: string) => {
    if (!phone) return '';
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 11) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    }
    return phone;
  };

  const getBusinessTypeIcon = (type: string) => {
    switch (type) {
      case 'hospital': return '🏥';
      case 'individual': return '👨‍⚕️';
      default: return '🏥';
    }
  };

  const getBusinessTypeLabel = (type: string) => {
    switch (type) {
      case 'hospital': return 'Hospital';
      case 'individual': return 'Profissional';
      default: return 'Clínica';
    }
  };

  const getLoadingMessage = () => {
    switch (searchStatus) {
      case 'typing':
        return {
          icon: '✍️',
          message: 'Digite nome e sobrenome do profissional (ex: Dr João Silva)...',
          color: '#6b7280'
        };
      case 'searching':
        return {
          icon: '🔍',
          message: 'Buscando profissionais... Aguarde...',
          color: '#2563eb'
        };
      case 'error':
        return {
          icon: '🔍',
          message: 'Buscando mais profissionais... Continue digitando se não encontrar.',
          color: '#6b7280'
        };
      default:
        return null;
    }
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-full" 
      style={{
        transition: isMobileDevice ? 'all 0.3s ease' : 'none',
        ...(isMobileDevice && isMobileInputFocused ? {
          position: 'fixed',
          top: 'auto',
          bottom: '20px',
          left: '16px',
          right: '16px',
          zIndex: 9999,
          backgroundColor: 'white',
          padding: '16px',
          borderRadius: '24px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        } : {})
      }}
    >
      {/* Loading/Status Message */}
      {!selectedBusiness && searchStatus !== 'idle' && searchStatus !== 'results' && (
        <div style={{
          position: 'relative',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '1rem',
          padding: '1.5rem',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            fontSize: '1.1rem',
            color: getLoadingMessage()?.color || '#6b7280'
          }}>
            {searchStatus === 'searching' ? (
              <div style={{
                width: '24px',
                height: '24px',
                border: '3px solid #e5e7eb',
                borderTop: '3px solid #2563eb',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <span style={{ fontSize: '1.5rem' }}>
                {getLoadingMessage()?.icon}
              </span>
            )}
            <span style={{ fontWeight: '500' }}>
              {getLoadingMessage()?.message}
            </span>
          </div>
          
          {searchStatus === 'typing' && (
            <div style={{
              marginTop: '0.5rem',
              fontSize: '0.9rem',
              color: '#9ca3af'
            }}>
              Digite pelo menos 2 caracteres para iniciar a busca
            </div>
          )}
        </div>
      )}

      {/* Suggestions Dropdown - Above Input - só mostrar se não há negócio selecionado */}
      {!selectedBusiness && suggestions.length > 0 && (
        <div style={{
          position: isMobileDevice && isMobileInputFocused ? 'absolute' : 'relative',
          bottom: isMobileDevice && isMobileInputFocused ? '100%' : 'auto',
          left: isMobileDevice && isMobileInputFocused ? '0' : 'auto',
          right: isMobileDevice && isMobileInputFocused ? '0' : 'auto',
          width: isMobileDevice && isMobileInputFocused ? '100%' : 'auto',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '1rem',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
          maxHeight: isMobileDevice && isMobileInputFocused ? '60vh' : '300px',
          overflowY: 'auto',
          zIndex: 1000,
          marginBottom: isMobileDevice && isMobileInputFocused ? '16px' : '1rem'
        }}>
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                padding: '1rem 1.5rem',
                cursor: 'pointer',
                borderBottom: index < suggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                backgroundColor: index === activeSuggestionIndex ? '#f8fafc' : 'white',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                if (index !== activeSuggestionIndex) {
                  e.currentTarget.style.backgroundColor = 'white';
                }
              }}
            >
              {suggestion.placeId === 'searching_placeholder' ? (
                // Item especial de loading com spinner
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.5rem 0' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid #e5e7eb',
                    borderTop: '3px solid #2563eb',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  
                  <div style={{ flex: 1 }}>
                    <h4 style={{ 
                      fontWeight: 'bold', 
                      color: '#2563eb', 
                      fontSize: '1.1rem',
                      marginBottom: '0.5rem'
                    }}>
                      {suggestion.name}
                    </h4>
                    <p style={{ 
                      color: '#6b7280', 
                      fontSize: '0.9rem',
                      margin: 0
                    }}>
                      {suggestion.address}
                    </p>
                  </div>
                </div>
              ) : (
                // Item normal de resultado
                <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                  <div style={{ fontSize: '2rem' }}>
                    {getBusinessTypeIcon(suggestion.type)}
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <h4 style={{ 
                      fontWeight: 'bold', 
                      color: '#111827', 
                      fontSize: '1.1rem',
                      marginBottom: '0.5rem'
                    }}>
                      {suggestion.name}
                    </h4>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <MapPin size={16} color="#6b7280" />
                    <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                      {suggestion.address}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    {suggestion.rating > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Star size={16} color="#f59e0b" fill="#f59e0b" />
                        <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                          {suggestion.rating}/5 ({suggestion.totalReviews} avaliações)
                        </span>
                      </div>
                    )}
                    
                    {suggestion.phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Phone size={16} color="#6b7280" />
                        <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                          {formatPhone(suggestion.phone)}
                        </span>
                      </div>
                    )}
                    
                    {suggestion.hours && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Clock size={16} color="#6b7280" />
                        <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                          {suggestion.hours}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '1rem',
                      fontSize: '0.8rem',
                      fontWeight: '500'
                    }}>
                      Profissional
                    </span>
                  </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div style={{ position: 'relative', marginBottom: '2rem' }}>
        <div style={{ 
          position: 'absolute',
          left: '2rem',
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#6b7280',
          fontSize: '1.5rem'
        }}>
          <Search size={24} />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Ex: Dr. João Silva, Clínica Odontológica Santos, Dra. Ana Nutricionista..."
          style={{
            width: '100%',
            paddingLeft: '4rem',
            paddingRight: '4rem',
            paddingTop: '2rem',
            paddingBottom: '2rem',
            fontSize: '1.375rem',
            border: '3px solid #2563eb',
            borderRadius: '1.5rem',
            outline: 'none',
            transition: 'all 0.3s ease',
            boxShadow: '0 10px 30px rgba(37, 99, 235, 0.2)',
            fontWeight: '500',
            textAlign: query ? 'left' : 'center'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#1d4ed8';
            e.target.style.boxShadow = '0 0 0 8px rgba(59, 130, 246, 0.15)';
            e.target.style.textAlign = 'left';
            handleInputFocus();
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#2563eb';
            e.target.style.boxShadow = '0 10px 30px rgba(37, 99, 235, 0.2)';
            if (!e.target.value) {
              e.target.style.textAlign = 'center';
            }
            // Não chamar handleInputBlur aqui - deixar o hook de clique fora gerenciar
            // Isso evita conflitos com seleções de sugestões
          }}
        />

        {/* Loading indicator - more persistent */}
        {(isLoading || searchStatus === 'searching' || searchStatus === 'typing') && (
          <div style={{
            position: 'absolute',
            right: '2rem',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {searchStatus === 'searching' ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #e5e7eb',
                  borderTop: '2px solid #2563eb',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span style={{ 
                  fontSize: '0.8rem', 
                  color: '#2563eb',
                  fontWeight: '500'
                }}>
                  Buscando...
                </span>
              </>
            ) : searchStatus === 'typing' ? (
              <>
                <div style={{
                  width: '6px',
                  height: '6px',
                  backgroundColor: '#10b981',
                  borderRadius: '50%',
                  animation: 'pulse 2s infinite'
                }} />
                <span style={{ 
                  fontSize: '0.8rem', 
                  color: '#10b981',
                  fontWeight: '500'
                }}>
                  Digite mais...
                </span>
              </>
            ) : (
              <Loader2 size={20} className="animate-spin" style={{ color: '#2563eb' }} />
            )}
          </div>
        )}

        {/* Clear button */}
        {query && !isLoading && searchStatus !== 'searching' && searchStatus !== 'typing' && (
          <button
            onClick={clearInput}
            style={{
              position: 'absolute',
              right: '2rem',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6b7280'
            }}
          >
            <X size={24} />
          </button>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.1); }
        }
        
        /* Overlay para modo mobile fixo */
        ${isMobileDevice && isMobileInputFocused ? `
          body::before {
            content: '';
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 9998;
            pointer-events: none;
          }
        ` : ''}
        
        /* Suporte para viewport units no mobile */
        @supports (-webkit-touch-callout: none) {
          .mobile-fixed-input {
            bottom: env(safe-area-inset-bottom, 20px);
          }
        }
      `}</style>
    </div>
  );
};

export default SmartSearchField;